import "leaflet/dist/leaflet.css";
import { Button } from "components/ButtonNew";
import { IconButton } from "components/IconButton";
import { InvalidateMapSize } from "components/map/InvalidateMapSize";
import { MapLayers } from "components/map/MapLayers";
import { useBrowserFullscreen } from "hooks/useBrowserFullscreen";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, useMap, useMapEvents } from "react-leaflet";
import { cn } from "utils/styling";

import type { AddressInfo, Coordinates, MapAddressSuggestion } from "../../model/types";

import { AddressMapMarker } from "./AddressMapMarker";
import { AddressSearchAutocomplete } from "./AddressSearchAutocomplete";
import { MAP_MOVE_ANIMATION_DURATION_SECONDS } from "./constants";

const MapMoveHandler = ({ onMapMove }: { onMapMove: (coords: Coordinates) => void }) => {
  useMapEvents({
    moveend: (e) => {
      const center = e.target.getCenter();
      onMapMove({ lat: center.lat, lng: center.lng });
    },
  });
  return null;
};

const RecenterMap = ({ center, recenterRequest }: { center: Coordinates; recenterRequest: number | undefined }) => {
  const map = useMap();
  const latestCenterRef = useRef(center);

  useEffect(() => {
    latestCenterRef.current = center;
  }, [center]);

  useEffect(() => {
    if (!recenterRequest) return;
    const latestCenter = latestCenterRef.current;
    if (!latestCenter) return;
    map.flyTo([latestCenter.lat, latestCenter.lng], map.getZoom(), {
      duration: MAP_MOVE_ANIMATION_DURATION_SECONDS,
    });
  }, [recenterRequest, map]);
  return null;
};

const AddressInfoBox = ({ addressInfo, className }: { addressInfo?: AddressInfo; className?: string }) => {
  if (!addressInfo?.address) return null;
  return (
    <div className={cn("rounded-md bg-lds-surface-bright px-4 py-2 shadow-elevation400", className)}>
      <span className="font-ds-body-regular-xs">
        {[addressInfo?.address, [addressInfo?.postal_code, addressInfo?.city].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", ")}
      </span>
    </div>
  );
};

type InteractiveLocationMapProps = {
  addressInfo?: AddressInfo;
  center: Coordinates;
  className?: string;
  fetchAddressLabel?: string;
  isFetchingAddress?: boolean;
  isReadOnly?: boolean;
  onFetchAddress?: () => void;
  onMapMove?: (coords: Coordinates) => void;
  onPickAddressSuggestion?: (suggestion: MapAddressSuggestion) => void;
  recenterRequest?: number;
  zoomLevel?: number;
};

export const InteractiveLocationMap = ({
  addressInfo,
  center,
  className,
  fetchAddressLabel,
  isFetchingAddress = false,
  isReadOnly = false,
  onFetchAddress,
  onMapMove,
  onPickAddressSuggestion,
  recenterRequest,
  zoomLevel,
}: InteractiveLocationMapProps) => {
  const { t } = useTranslation();

  const { isFullscreen, ref: mapShellRef, toggleFullscreen } = useBrowserFullscreen<HTMLDivElement>();

  return (
    <div className="relative h-full w-full [&:fullscreen]:min-h-screen" ref={mapShellRef}>
      {onPickAddressSuggestion && (
        <AddressSearchAutocomplete
          className="absolute left-1/2 top-3 z-object_on_map w-4/5 -translate-x-1/2"
          disabled={isReadOnly}
          onSelect={onPickAddressSuggestion}
          popperDisablePortal={isFullscreen}
        />
      )}
      {onFetchAddress && fetchAddressLabel && (
        <Button
          className="absolute bottom-4 left-1/2 z-object_on_map -translate-x-1/2"
          isLoading={isFetchingAddress}
          onClick={onFetchAddress}
          variant="secondary"
        >
          {isFullscreen ? t("general.select") : fetchAddressLabel}
        </Button>
      )}
      <IconButton
        buttonProps={{
          className: "absolute right-4 bottom-4 z-object_on_map",
          onClick: toggleFullscreen,
          size: "md",
          variant: "secondary",
        }}
        iconProps={{
          iconName: isFullscreen ? "FullscreenExit" : "Fullscreen",
          size: "md",
        }}
      />
      <MapContainer
        boxZoom={!isReadOnly}
        center={[center.lat, center.lng]}
        className={cn("h-full w-full", className)}
        doubleClickZoom={!isReadOnly}
        dragging={!isReadOnly}
        keyboard={!isReadOnly}
        scrollWheelZoom={!isReadOnly}
        touchZoom={!isReadOnly}
        zoom={zoomLevel}
      >
        <InvalidateMapSize sizeTrigger={isFullscreen} />
        <MapLayers defaultLayer="Standard" layers={["Satellite", "Standard"]} />
        {!isReadOnly && onMapMove && <MapMoveHandler onMapMove={onMapMove} />}
        <RecenterMap center={center} recenterRequest={recenterRequest} />
      </MapContainer>
      <AddressMapMarker className="absolute left-1/2 top-1/2 z-object_on_map -translate-x-1/2 -translate-y-full" />
      <AddressInfoBox
        addressInfo={addressInfo}
        className="absolute left-1/2 top-1/2 z-object_on_map -translate-x-1/2 -translate-y-22"
      />
    </div>
  );
};
